import React from 'react';
import useLocalStorageState from './useLocalStorageState';

const STORAGE_KEY = 'server_time_difference';
const SYNC_INTERVAL_MS = 5 * 60 * 1000;
const FETCH_TIMEOUT_MS = 10000;
const MIN_RELEVANT_DIFFERENCE_MS = 60000;

let cachedRaw: string | null = null;
let cachedDifference = 0;

export const readServerTimeDifference = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (raw !== cachedRaw) {
      cachedRaw = raw;
      const value = raw ? JSON.parse(raw)?.value : 0;
      cachedDifference = Number.isFinite(value) ? value : 0;
    }
  } catch {
    cachedRaw = null;
    cachedDifference = 0;
  }

  return cachedDifference;
};

const useServerTime = () => {
  const [difference, setTime] = useLocalStorageState(STORAGE_KEY, 0);

  React.useEffect(() => {
    let active = true;
    let currentController: AbortController | null = null;

    const syncServerTime = async () => {
      const controller = new AbortController();
      currentController = controller;
      const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

      try {
        const response = await fetch('https://gnaut.me/current_server_time', {
          signal: controller.signal,
        });
        const data = await response.json();

        if (!active || !Number.isFinite(data?.serverTime)) return;

        const difference = data.serverTime - Date.now();

        setTime(
          Math.abs(difference) < MIN_RELEVANT_DIFFERENCE_MS ? 0 : difference
        );
      } catch {
        // A failed sync must not touch the stored difference: resetting it
        // to 0 would undo a previously correct offset on devices with a
        // skewed clock, which then create instantly-expired reservations.
      } finally {
        clearTimeout(timeout);
      }
    };

    syncServerTime();

    const interval = setInterval(syncServerTime, SYNC_INTERVAL_MS);
    window.addEventListener('online', syncServerTime);

    return () => {
      active = false;
      currentController?.abort();
      clearInterval(interval);
      window.removeEventListener('online', syncServerTime);
    };
  }, []);

  return difference ?? 0;
};

export default useServerTime;
